import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useCallback, useMemo } from 'react'

import {
  discardDraft,
  getEmailThread,
  listEmailDrafts,
  listEmailThreads,
  saveDraft,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  DiscardDraftRequestSchema,
  GetEmailThreadRequestSchema,
  ListEmailDraftsRequestSchema,
  SaveDraftRequestSchema,
} from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { organizationStore } from './organization.store'

export function useSaveEmailDraft() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(saveDraft, {
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
      if (variables.threadId) {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getEmailThread,
            transport,
            input: create(GetEmailThreadRequestSchema, {
              id: variables.threadId,
            }),
            cardinality: 'finite',
          }),
        })
      }
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailDrafts,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  /**
   * Save (create or update) a draft. Identity resolution mirrors the server:
   * `draftId` set -> update that draft; else `threadId` set -> upsert the
   * user's single reply draft on that thread; else create a new compose draft.
   */
  const save = useCallback(
    async (params: {
      organizationId: string
      threadId?: string
      draftId?: string
      parentType?: ParentTypeEnum
      parentId?: string
      fromAccountId?: string
      toEmails?: string[]
      ccEmails?: string[]
      bccEmails?: string[]
      subject: string
      body: string
      attachmentIds?: string[]
      /**
       * Optimistic-concurrency token — the `EmailDraft.updated_at` this client
       * last saw for `draftId`/`threadId`. Set it to have a stale save (another
       * tab saved in between) rejected with `FailedPrecondition` instead of
       * silently overwriting it. Leave unset for last-write-wins.
       */
      expectedUpdatedAt?: Timestamp
    }) => {
      const request = create(SaveDraftRequestSchema, {
        ...params,
        toEmails: params.toEmails ?? [],
        ccEmails: params.ccEmails ?? [],
        bccEmails: params.bccEmails ?? [],
        attachmentIds: params.attachmentIds ?? [],
      })
      return mutation.mutateAsync(request)
    },
    [mutation.mutateAsync],
  )

  return {
    mutation,
    saveDraft: save,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useDiscardEmailDraft() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(discardDraft, {
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
      if (variables.threadId) {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getEmailThread,
            transport,
            input: create(GetEmailThreadRequestSchema, {
              id: variables.threadId,
            }),
            cardinality: 'finite',
          }),
        })
      }
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailDrafts,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  /** Discard a draft. Supply `draftId`, or `threadId` for a reply draft. */
  const discard = useCallback(
    async (params: {
      organizationId: string
      threadId?: string
      draftId?: string
    }) => {
      const request = create(DiscardDraftRequestSchema, params)
      return mutation.mutateAsync(request)
    },
    [mutation.mutateAsync],
  )

  return {
    mutation,
    discardDraft: discard,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export interface UseListEmailDraftsOptions {
  /** Return only compose drafts that have no thread (excludes reply drafts). */
  composesOnly?: boolean
  parentType?: ParentTypeEnum
  parentId?: string
  limit?: number
  offset?: number
  enabled?: boolean
}

/**
 * Hook to list the current user's saved drafts (compose and/or reply), so an
 * interrupted compose can be resumed from any session or entity page.
 */
export function useListEmailDrafts(options: UseListEmailDraftsOptions = {}) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId,
  )

  const request = useMemo(
    () =>
      create(ListEmailDraftsRequestSchema, {
        organizationId: selectedOrganizationId || '',
        composesOnly: options.composesOnly,
        parentType: options.parentType,
        parentId: options.parentId,
        limit: options.limit,
        offset: options.offset,
      }),
    [
      selectedOrganizationId,
      options.composesOnly,
      options.parentType,
      options.parentId,
      options.limit,
      options.offset,
    ],
  )

  const query = useQuery(listEmailDrafts, request, {
    enabled: options.enabled !== false && !!selectedOrganizationId,
    staleTime: 30 * 1000,
  })

  return {
    drafts: query.data?.drafts ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}
